import React, { useEffect, useState } from "react";
import { 
    Box, 
    Grid, 
    Typography,
    Tabs,
    Tab,
    Stack
} from "@mui/material";
import WarningIcon from '@mui/icons-material/Warning';
import { toast } from "react-toastify";
import BenefitCard from "../../../v2/Benefits/Beneficiary/Components/BenefitCard";
import FirmDetails from "../../../v2/Benefits/Beneficiary/Components/FirmDetails";
import { CustomButton } from "../../../v2/MUI_custom_component";
import ReusableModal from "../../../v2/MUI_custom_component/Modal/CustomModal";
import InlineBeneficiaryForm from "./Components/InlineBeneficiaryForm";
import StaffCard from "./Components/StaffCard";
import { AddStaffBeneficiaryAction, AddStaffNomineeAction, deleteStaffAction, getStaffInsuranceAction } from "./APIs/action";
import { useLocation } from "react-router-dom";

const StaffInsurance = () => {
    const location = useLocation();
    const selectedCompanyEntity = location?.state?.selectedCompanyEntity || '';
    const [staffInsuranceData, setStaffInsuranceData] = useState([]);
    const [loader, setLoader] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteContext, setDeleteContext] = useState(null); // { type: 'beneficiary' | 'nominee' }
    const [currentTab, setCurrentTab] = useState(0);
    const { benefit_card, selected_firm, staff_details, relations } = staffInsuranceData || {};

    const handleDeleteStaff = (deleteType = 'beneficiary') => {
        setDeleteContext({ type: deleteType });
        setDeleteDialogOpen(true);
    };
    const getStaffInsuranceData = async () => {
        setLoader(true);
        const {error, message, data} = await getStaffInsuranceAction(selectedCompanyEntity);
        setLoader(false);
        if (!error) {
            setStaffInsuranceData(data);
        } else {
            toast.error(message);
        }
    };
    const buildPayload = (data, currentTab, isNominee = true, beneficiary_id) => {
        const basePayload = {
            staff_no: data.staff_no,
            id_type: data.document_id ? 1 : null ,
            full_name: data.full_name,
            dob: data.dob,
            age: data.age,
            phone_number: data.phone_number,
            gender: data.gender ? data.gender.toUpperCase() : null,
            ocr_verified: data.ocr_verified || false,
            document_no: data.document_id || null,
            company_esi_id: selectedCompanyEntity,
        };
        if (isNominee) {
            basePayload.relation = data.relation;
            basePayload.beneficiary_id = beneficiary_id;
        }
        return basePayload;
    };
    const handleSaveBeneficiary = async(beneficiaryData) => {
        const beneficiaryPayload = buildPayload(beneficiaryData, currentTab, false);
        const { error, message } = await AddStaffBeneficiaryAction(beneficiaryPayload);
        if (!error) {
            toast.success(message);
            getStaffInsuranceData()
        } else {
            toast.error(message);
        }
    };
    const handleSaveNominee = async(nomineeData, beneficiary_id) => {
        const nomineePayload = buildPayload(nomineeData, currentTab, true, beneficiary_id);
        const { error, message } = await AddStaffNomineeAction(nomineePayload);
        if (!error) {
            toast.success(message);
            getStaffInsuranceData()
        } else {
            toast.error(message);
        }
    };

    useEffect(() => {   
        getStaffInsuranceData();
    }, []);

    if (loader) {
        return (
            <Box sx={{ p: { xs: 1.5, md: 3 } }}>
                <Typography variant="body2" color="text.secondary">
                    Loading staff insurance details...
                </Typography>
            </Box>
        );
    }   

    return (
        <Box sx={{ p: { xs: 1.5, md: 3 } }}>
            <Grid container spacing={2} sx={{ alignItems: 'stretch', mb: 3 }}>
                <Grid item xs={12} sm={6}>
                    <BenefitCard item={benefit_card} sx={{ height: '100%' }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box
                        sx={{
                            background: '#fff',
                            p: 2,
                            borderRadius: 2,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-evenly',
                        }}
                    >
                        {selected_firm?.length > 0 ? (
                            selected_firm.map((detail, index) => (
                                <FirmDetails key={detail?.id || index} detail={detail} />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No firm details available
                            </Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Box>
                        <Box>
                            {staff_details?.length > 0  && (
                                <Box>
                                    <Tabs 
                                        value={currentTab} 
                                        onChange={(event, newValue) => setCurrentTab(newValue)}
                                        sx={{ 
                                            borderBottom: 1, 
                                            borderColor: 'divider',
                                            mb: 3
                                        }}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        {staff_details.map((staff, index) => (
                                            <Tab 
                                                key={staff.staff_no}
                                                label={staff.staff_label} 
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    minWidth: 120,
                                                }}
                                            />
                                        ))}
                                    </Tabs>
                                    {staff_details.map((staff, index) => (
                                        <Box
                                            key={staff.staff_no}
                                            role="tabpanel"
                                            hidden={currentTab !== index}
                                            sx={{ 
                                                display: currentTab === index ? 'block' : 'none' 
                                            }}
                                        >
                                            {currentTab === index && (
                                                <Box>
                                                    {(() => {
                                                        const hasValidBeneficiary = staff.beneficiary && 
                                                            Object.keys(staff.beneficiary).length > 0 && 
                                                            staff.beneficiary.full_name;
                                                        
                                                        if (!hasValidBeneficiary) {
                                                            return (
                                                                <Grid container spacing={2}>
                                                                    <Grid item xs={12} md={6}>
                                                                        <InlineBeneficiaryForm
                                                                            staffData={staff}
                                                                            onSave={handleSaveBeneficiary}
                                                                            relations={relations}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            );
                                                        }
                                                        return (
                                                            <StaffCard
                                                                staffData={staff}
                                                                onDelete={handleDeleteStaff}
                                                                onSaveNominee={handleSaveNominee}
                                                                relations={relations}
                                                            />
                                                        );
                                                    })()}
                                                </Box>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            <ReusableModal
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setDeleteContext(null);
                }}
                title=""
                maxWidth="500px"
                scrollable={false}
            >
                <Box 
                    sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        py: 2
                    }}>
                        <WarningIcon sx={{ fontSize: 80, color: '#ff9800', mb: 2 }} />
                        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                            Are you sure you want to delete the {deleteContext?.type || 'staff'} details for {staff_details?.[currentTab]?.staff_label || 'this staff member'}? 
                            This action cannot be undone.
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ width: '100%', gap: 2, justifyContent: 'center' }}>
                            <CustomButton
                                variant="outlined"
                                onClick={() => {
                                    setDeleteDialogOpen(false);
                                    setDeleteContext(null);
                                }}
                                disabled={loader}
                                label="Cancel"
                            />
                            <CustomButton
                                variant="filled"
                                color="primary"
                                onClick={async () => {
                                    if (!deleteContext || !staff_details?.[currentTab]) return;
                                    
                                    const isBeneficiary = deleteContext.type === 'beneficiary';
                                    const isNominee = deleteContext.type === 'nominee';
                                    const staffNo = staff_details[currentTab].staff_no;
                                    
                                    setLoader(true);
                                    const response = await deleteStaffAction(isBeneficiary, isNominee, staffNo, selectedCompanyEntity);
                                    const { error, message } = response;
                                    setLoader(false);
                                    
                                    if (!error) {
                                        toast.success(message);
                                        await getStaffInsuranceData();
                                    } else {
                                        toast.error(message);
                                    }
                                    setDeleteDialogOpen(false);
                                    setDeleteContext(null);
                                }}

                                disabled={loader}
                                label={loader ? "Deleting..." : "Delete"}
                            />
                        </Stack>
                </Box>
               
            </ReusableModal>
        </Box>
    );
};

export default StaffInsurance;